import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useSettings } from '../context/SettingsContext';
import { getProducts, Product } from '../api/products';
import { generateGeminiResponse } from '../api/gemini';

interface Message {
    id: number;
    text: string;
    sender: 'user' | 'bot';
    suggestions?: { title: string, url: string }[];
}

type Tab = 'whatsapp' | 'chatbot';

const FormattedMessage: React.FC<{ text: string }> = ({ text }) => {
    // Split by bold markers (**text**)
    const parts = text.split(/(\*\*.*?\*\*)/g);

    return (
        <span className="whitespace-pre-wrap font-normal">
            {parts.map((part, index) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                    return <strong key={index} className="font-bold">{part.slice(2, -2)}</strong>;
                }
                return part;
            })}
        </span>
    );
};

const UnifiedChatWidget: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { getSetting } = useSettings();
    const isRTL = i18n.dir() === 'rtl';

    // UI State
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<Tab>('chatbot');
    const [showHistory, setShowHistory] = useState(false);

    // Persisted Data Types
    interface ChatSession {
        id: number;
        timestamp: number;
        summary: string;
        messages: Message[];
    }

    // Chatbot logic state
    const [sessions, setSessions] = useState<ChatSession[]>([]);
    const [currentSessionId, setCurrentSessionId] = useState<number>(Date.now());
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);

    // Load Sessions and Auto-open on first visit
    useEffect(() => {
        const savedSessions = localStorage.getItem('promoshop_chat_sessions');
        if (savedSessions) {
            try {
                const parsed: ChatSession[] = JSON.parse(savedSessions);
                // Filter out corrupted sessions
                const validSessions = parsed.filter(s =>
                    s.messages &&
                    s.messages.length > 0 &&
                    !s.summary.startsWith('undefined')
                );

                setSessions(validSessions);

                // Load the most recent session if available and valid
                if (validSessions.length > 0) {
                    const lastSession = validSessions[validSessions.length - 1];
                    setMessages(lastSession.messages);
                    setCurrentSessionId(lastSession.id);
                }
            } catch (e) {
                console.error("Failed to parse chat history", e);
            }
        }

        // Auto-open on first visit
        const hasVisited = localStorage.getItem('promoshop_chat_visited');
        if (!hasVisited) {
            setIsOpen(true);
            localStorage.setItem('promoshop_chat_visited', 'true');
        }
    }, []);

    // Save Sessions to LocalStorage whenever messages change
    useEffect(() => {
        // Don't save empty sessions or sessions with only the initial greeting
        if (messages.length === 0) return;
        if (messages.length === 1 && messages[0].sender === 'bot') return;

        setSessions(prev => {
            const existingIndex = prev.findIndex(s => s.id === currentSessionId);
            const userMsg = messages.find(m => m.sender === 'user');
            const summary = userMsg
                ? (userMsg.text.substring(0, 30) + (userMsg.text.length > 30 ? '...' : ''))
                : (isRTL ? 'محادثة جديدة' : 'New Conversation');

            const updatedSession: ChatSession = {
                id: currentSessionId,
                timestamp: Date.now(),
                summary: summary,
                messages: messages
            };

            const newSessions = [...prev];
            if (existingIndex >= 0) {
                newSessions[existingIndex] = updatedSession;
            } else {
                newSessions.push(updatedSession);
            }

            localStorage.setItem('promoshop_chat_sessions', JSON.stringify(newSessions));
            return newSessions;
        });
    }, [messages, currentSessionId, isRTL]);

    const handleNewChat = () => {
        setMessages([{ id: Date.now(), text: t('chatbot.greeting'), sender: 'bot' }]);
        setCurrentSessionId(Date.now());
        setShowHistory(false);
    };

    const handleLoadSession = (session: ChatSession) => {
        setMessages(session.messages);
        setCurrentSessionId(session.id);
        setShowHistory(false);
    };

    // WhatsApp logic state (Restored)
    const [waMessage, setWaMessage] = useState('');
    const phoneNumber = '212637131815';
    const defaultWaMessage = isRTL
        ? getSetting('whatsapp_message_ar', 'مرحبا! أود الاستفسار عن منتجاتكم.')
        : getSetting('whatsapp_message_fr', 'Bonjour! Je voudrais me renseigner sur vos produits.');

    // ... typing state etc
    const [products, setProducts] = useState<Product[]>([]);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Fetch Products on mount
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const data = await getProducts();
                setProducts(data || []);
            } catch (error) {
                console.error("Failed to fetch products for chatbot context", error);
            }
        };
        fetchProducts();
    }, []);

    // Initialize Chatbot Greeting
    useEffect(() => {
        if (messages.length === 0) {
            setMessages([
                { id: 1, text: t('chatbot.greeting'), sender: 'bot' }
            ]);
        }
    }, [t, messages.length]);

    // Scroll to bottom effect
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (activeTab === 'chatbot' && isOpen) {
            scrollToBottom();
        }
    }, [messages, isOpen, isTyping, activeTab]);

    // WhatsApp Handlers
    const handleSendWhatsApp = () => {
        const messageToSend = waMessage || defaultWaMessage;
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(messageToSend)}`;
        window.open(whatsappUrl, '_blank');
        setWaMessage('');
        // Optional: Close widget after sending to WA? User might want to stay. Let's keep it open.
        // setIsOpen(false); 
    };

    // Chatbot Handlers
    const handleSendChatbot = async () => {
        if (!inputValue.trim()) return;

        const userText = inputValue;
        setInputValue('');

        const newUserMessage: Message = {
            id: Date.now(),
            text: userText,
            sender: 'user'
        };

        setMessages(prev => [...prev, newUserMessage]);
        setIsTyping(true);

        try {
            const response = await generateGeminiResponse(
                userText,
                products,
                i18n.language.startsWith('ar') ? 'ar' : 'fr'
            );

            // Handle Handoff
            if (response.handoffSummary) {
                setWaMessage(response.handoffSummary);
            }

            const botResponse: Message = {
                id: Date.now() + 1,
                text: response.text,
                sender: 'bot',
                suggestions: response.suggestions
            };
            setMessages(prev => [...prev, botResponse]);
        } catch (error) {
            console.error("Chatbot Error:", error);
            const errorMsg: Message = {
                id: Date.now() + 1,
                text: isRTL ? "عذراً، حدث خطأ." : "Désolé, une erreur s'est produite.",
                sender: 'bot'
            };
            setMessages(prev => [...prev, errorMsg]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendChatbot();
        }
    };

    // Dispatch event when isOpen changes
    useEffect(() => {
        const event = new CustomEvent('chatWidgetStateChange', { detail: { isOpen } });
        window.dispatchEvent(event);
    }, [isOpen]);

    return (
        <div className={`fixed bottom-6 z-50 ${isRTL ? 'left-6' : 'right-6'}`}>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: 20 }}
                        transition={{ duration: 0.3 }}
                        className="mb-4 bg-white rounded-3xl shadow-elevated overflow-hidden border border-gray-100 flex flex-col w-80 sm:w-96"
                        style={{ height: '500px' }}
                    >
                        {/* Header & Tabs */}
                        <div className="bg-gradient-primary p-4 pb-0 text-white">
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center gap-2">
                                    <h5 className="font-bold text-lg">
                                        {activeTab === 'whatsapp' ? t('common.whatsappTitle') : t('chatbot.title')}
                                    </h5>
                                </div>
                                <div className="flex items-center gap-1">
                                    {activeTab === 'chatbot' && (
                                        <>
                                            <button
                                                onClick={() => setShowHistory(!showHistory)}
                                                className="text-white/80 hover:text-white transition-colors p-1"
                                                title="History"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={handleNewChat}
                                                className="text-white/80 hover:text-white transition-colors p-1"
                                                title="New Chat"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                </svg>
                                            </button>
                                        </>
                                    )}
                                    <button
                                        onClick={() => setIsOpen(false)}
                                        className="text-white/80 hover:text-white transition-colors p-1"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* Tabs */}
                            <div className="flex gap-1 overflow-x-auto no-scrollbar">
                                <button
                                    onClick={() => setActiveTab('whatsapp')}
                                    className={`flex-1 py-3 px-4 text-sm font-medium rounded-t-lg transition-colors flex items-center justify-center gap-2 ${activeTab === 'whatsapp'
                                        ? 'bg-white text-primary'
                                        : 'bg-white/10 text-white hover:bg-white/20'
                                        }`}
                                >
                                    {/* WA Icon */}
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                                    </svg>
                                    WhatsApp
                                </button>
                                <button
                                    onClick={() => setActiveTab('chatbot')}
                                    className={`flex-1 py-3 px-4 text-sm font-medium rounded-t-lg transition-colors flex items-center justify-center gap-2 ${activeTab === 'chatbot'
                                        ? 'bg-white text-primary'
                                        : 'bg-white/10 text-white hover:bg-white/20'
                                        }`}
                                >
                                    {/* Bot Icon */}
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                    </svg>
                                    {t('chatbot.title')}
                                </button>
                            </div>
                        </div>

                        {/* Content Area */}

                        {/* WhatsApp Tab Content */}
                        {activeTab === 'whatsapp' && (
                            <div className="flex-1 flex flex-col p-6 bg-white overflow-y-auto">
                                <div className="flex flex-col items-center justify-center text-center space-y-4 mb-6 mt-4">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900 text-lg mb-1">{t('common.whatsappOnline')}</h3>
                                        <p className="text-gray-500 text-sm">{t('common.whatsappGreeting')}</p>
                                    </div>
                                </div>

                                <div className="mt-auto space-y-3">
                                    <textarea
                                        value={waMessage}
                                        onChange={(e) => setWaMessage(e.target.value)}
                                        placeholder={defaultWaMessage}
                                        className={`w-full px-4 py-3 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none bg-gray-50 ${isRTL ? 'text-right' : 'text-left'}`}
                                        rows={3}
                                    />
                                    <button
                                        onClick={handleSendWhatsApp}
                                        className="w-full bg-[#25D366] text-white font-bold py-3 px-6 rounded-xl hover:shadow-lg transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                        </svg>
                                        {t('common.sendMessage')}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Chatbot Tab Content */}
                        {activeTab === 'chatbot' && (
                            showHistory ? (
                                <div className="flex-1 overflow-y-auto p-4 bg-gray-50 flex flex-col gap-2">
                                    <h3 className="font-bold text-gray-700 mb-2 px-2">
                                        {isRTL ? 'المحادثات السابقة' : 'Past Conversations'}
                                    </h3>
                                    {sessions.length === 0 ? (
                                        <div className="text-center text-gray-400 mt-10">
                                            {isRTL ? 'لا توجد محادثات سابقة' : 'No past conversations'}
                                        </div>
                                    ) : (
                                        [...sessions].reverse().map(session => (
                                            <button
                                                key={session.id}
                                                onClick={() => handleLoadSession(session)}
                                                className={`p-4 rounded-xl text-left transition-all group hover:bg-white hover:shadow-md border border-transparent hover:border-gray-100 ${session.id === currentSessionId ? 'bg-white shadow-sm border-primary/20 ring-1 ring-primary/20' : 'bg-transparent'
                                                    }`}
                                            >
                                                <div className="flex justify-between items-center mb-1">
                                                    <span className="text-xs text-gray-400 font-medium">
                                                        {new Date(session.timestamp).toLocaleDateString()}
                                                    </span>
                                                    {session.id === currentSessionId && (
                                                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-700 font-medium line-clamp-2 leading-relaxed group-hover:text-primary transition-colors">
                                                    {session.summary}
                                                </p>
                                            </button>
                                        ))
                                    )}
                                </div>
                            ) : (
                                <>
                                    <div className="flex-1 overflow-y-auto p-4 bg-gray-50 flex flex-col gap-3">
                                        {messages.map((msg) => (
                                            <div
                                                key={msg.id}
                                                className={`max-w-[85%] p-3 rounded-2xl text-sm leading-relaxed shadow-sm ${msg.sender === 'user'
                                                    ? 'bg-primary text-white self-end rounded-br-none'
                                                    : 'bg-white text-gray-800 self-start rounded-bl-none border border-gray-100'
                                                    }`}
                                            >
                                                <FormattedMessage text={msg.text} />
                                                {msg.suggestions && msg.suggestions.length > 0 && (
                                                    <div className="mt-3 pt-3 border-t border-white/20 flex flex-col gap-2">
                                                        {msg.suggestions.slice(0, 3).map((suggestion, index) => (
                                                            <a
                                                                key={index}
                                                                href={suggestion.url}
                                                                className={`block w-full py-2.5 px-4 rounded-xl font-bold text-center transition-all transform hover:-translate-y-0.5 shadow-sm text-sm ${msg.sender === 'user'
                                                                    ? 'bg-white text-primary hover:bg-gray-50'
                                                                    : 'bg-primary text-white hover:bg-primary-dark'
                                                                    }`}
                                                            >
                                                                {(isRTL ? 'اشتري: ' : 'Acheter: ') + suggestion.title}
                                                            </a>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}

                                        {isTyping && (
                                            <div className="bg-white text-gray-500 self-start shadow-sm rounded-2xl rounded-bl-none border border-gray-100 p-3 text-xs italic flex items-center gap-1">
                                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                                                <span className="ml-1">{t('chatbot.thinking')}</span>
                                            </div>
                                        )}
                                        <div ref={messagesEndRef} />
                                    </div>

                                    <div className="p-3 bg-white border-t border-gray-100">
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={inputValue}
                                                onChange={(e) => setInputValue(e.target.value)}
                                                onKeyPress={handleKeyPress}
                                                placeholder={t('chatbot.placeholder')}
                                                className={`flex-1 px-4 py-2 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 ${isRTL ? 'text-right' : 'text-left'}`}
                                                disabled={isTyping}
                                            />
                                            <button
                                                onClick={handleSendChatbot}
                                                disabled={!inputValue.trim() || isTyping}
                                                className="p-2 bg-primary text-white rounded-full hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {isTyping ? (
                                                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${isRTL ? 'transform rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Main Floating Button */}
            {
                !isOpen && (
                    <motion.button
                        onClick={() => setIsOpen(true)}
                        className="bg-gradient-primary text-white rounded-full w-16 h-16 flex items-center justify-center shadow-floating hover:shadow-glow-lg transition-smooth relative"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        animate={{
                            boxShadow: [
                                '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                                '0 0 40px rgba(16, 185, 129, 0.4)',
                                '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                            ],
                        }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: 'easeInOut',
                        }}
                    >
                        {/* Badge to show dual capability if desired, or just generic icon */}
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            {/* Chat Bubble Icon representing both */}
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>

                        {/* Notification Dot (optional) */}
                        <span className="absolute top-0 right-0 -mr-1 -mt-1 w-5 h-5 bg-red-500 rounded-full border-2 border-white"></span>
                    </motion.button>
                )
            }
        </div >
    );
};

export default UnifiedChatWidget;
